import { useCallback, useRef, useEffect } from 'react';

/**
 * useMatchChime — plays a pleasant notification chime when a new match arrives.
 * Audio is gated by a LocalStorage toggle ("audioAlertsEnabled").
 */

const CHIME_FREQUENCY = 880;  // A5
const CHIME_DURATION = 0.12;

function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    // Three-note ascending chime: C5 → E5 → G5
    const notes = [523.25, 659.25, 783.99];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.25, ctx.currentTime + i * CHIME_DURATION);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * CHIME_DURATION + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * CHIME_DURATION);
      osc.stop(ctx.currentTime + i * CHIME_DURATION + 0.4);
    });

    // Close context after chime finishes
    setTimeout(() => ctx.close(), 1500);
  } catch {
    // Web Audio not supported or blocked
  }
}

export function getAudioAlertsEnabled() {
  return localStorage.getItem('audioAlertsEnabled') !== 'false';
}

export function setAudioAlertsEnabled(enabled) {
  localStorage.setItem('audioAlertsEnabled', enabled ? 'true' : 'false');
}

export function useMatchChime() {
  const lastPlayedRef = useRef(0);

  const chime = useCallback(() => {
    if (!getAudioAlertsEnabled()) return;
    // Debounce: don't play more than once per second
    const now = Date.now();
    if (now - lastPlayedRef.current < 1000) return;
    lastPlayedRef.current = now;
    playChime();
  }, []);

  return { chime, playChime };
}

export default useMatchChime;
